import React, { ReactElement, useContext, useRef } from 'react'

import cx from 'classnames'

import { Avatar, Button, Div } from '@driftt/tide-core'

import Card from 'components/Card'
import TooltipHoverPortal from 'components/TooltipHoverPortal'
import { OrgUsersById, OrgUsersContext } from 'context/orgUsersContext'
import useSalesforceData from 'hooks/useSalesforceData'
import { ActivityGroupByEndUser, CompanyData } from 'modules/accountActivity/domain'
import { ReactComponent as LinkedInIcon } from 'svgs/linkedin.svg'
import { ReactComponent as SalesforceIcon } from 'svgs/salesforce.svg'
import { buildLinkedInSearchUrl } from 'utils/linkedin'

import getToolTipData from './getToolTipData'

import styles from './styles.module.css'

type ToolTipData = ActivityGroupByEndUser | CompanyData

interface ActivityContentToolTipProps {
  children: ReactElement | ReactElement[]
  className?: string
  data: ToolTipData | undefined
}

interface ToolTipContentProps {
  data: ToolTipData
}

const TooltipContent = ({ data }: ToolTipContentProps) => {
  const orgUsersById = useContext<OrgUsersById>(OrgUsersContext)
  const salesforceData = useSalesforceData(data)

  const { avatarProps, avatarLabel, subheader, activityInfo } =
    getToolTipData(data, orgUsersById) || {}

  const onClickLinkedIn = () => {
    const endUserDataObj = data as ActivityGroupByEndUser
    const companyDataObj = data as CompanyData

    const endUser = endUserDataObj?.endUserData
    const companyData = endUser ? endUserDataObj.activityList[0].accountData : companyDataObj
    const linkedInUrl = buildLinkedInSearchUrl(endUser, companyData)
    window.open(linkedInUrl)
  }

  const onClickSalesforce = () => {
    if (!salesforceData) {
      return
    }
    const baseUrl = salesforceData.baseUrl
    const path = salesforceData.contacts.length
      ? salesforceData.contacts[0].Id
      : salesforceData.accounts[0].Id

    window.open(`${baseUrl}${path}`)
  }

  return (
    <Card className={styles.TooltipCard}>
      <div className={styles.TooltipHeader}>
        <Avatar className={styles.AvatarIcon} {...avatarProps} />
        <div className={cx(styles.AvatarLabel, styles.TwoLineTruncated)}>{avatarLabel}</div>
      </div>

      {subheader}

      {activityInfo && <Div className={styles.ActivityInfoContainer}>{activityInfo}</Div>}

      <div>
        <Button className={styles.ActionButton} type="utility" onClick={onClickLinkedIn}>
          <LinkedInIcon />
        </Button>
        {salesforceData && (
          <Button className={styles.ActionButton} type="utility" onClick={onClickSalesforce}>
            <SalesforceIcon />
          </Button>
        )}
      </div>
    </Card>
  )
}

const ActivityContentToolTip = ({
  children,
  className,
  data
}: ActivityContentToolTipProps): ReactElement | any => {
  const toolTipRef = useRef(null)

  if (!data) {
    return children
  }

  return (
    <TooltipHoverPortal
      className={cx(styles.TooltipContainer, className)}
      boundaryRef={toolTipRef.current}
      refElement={toolTipRef.current}
      placement="right"
      tooltipBody={<TooltipContent data={data} />}>
      <span className={styles.TooltipContentContainer} ref={toolTipRef}>
        {children}
      </span>
    </TooltipHoverPortal>
  )
}

export default ActivityContentToolTip
